/**
 * eligibilityLimit.js — PROD-696 / PROD-691 / PROD-689 (patient P4, 2026-10-07). ONE READER FOR
 * EVERY ELIGIBILITY ANSWER, AND ONE RULE FOR WHEN A TYPED MEMBER ID IS CHECKED.
 *
 * ★★★ WHY. Every eligibility check is a PAID Stedi 270. orella-api holds a per-patient limit
 *   (PROD-589: 3 patient-triggered checks in 24 h), DORMANT until the live patient app (a) stops
 *   checking per keystroke and (b) reads the limit answers. Both live here.
 *
 * THE LIMIT ANSWERS (orella-api trigger_practice_eligibility, wiring steps in
 * d6b/notes/PROD-589_limit_dormant.md; the same keys for the case and secondary routes, PROD-689):
 *     HTTP 200 { eligibility: <the STORED result, or null>, error: 'recheck_limit',
 *                availableAt: <ISO time the next check is allowed> }
 *     HTTP 200 { eligibility: <stored or null>, error: 'recheck_unavailable', availableAt: null }
 *   200 because onboarding is never blocked. The stored result is the LAST real result: it is
 *   shown as it is, and a null leaves the card as it was.
 *
 *   eligibilityAnswer(resp)  -> { eligibility, limited: 'limit' | 'unavailable' | null, availableAt }
 *     `eligibility` is the answer's object or null (never a non-object); `availableAt` is a
 *     string only for 'limit'. Any other `error` (a soft fail such as no member ID) is not a
 *     limit: limited null, as today.
 *
 *   eligibilityKey(ins)      -> 'carrier|memberId', or null without both. carrier: a routable
 *     providerId, else providerId plus the normalized carrier name (review A2 / B1). When the
 *     patient is not the policyholder, plus the relationship and the policyholder's name and date
 *     of birth (PROD-822). The key onboarding uses for "checked once per coverage" and Profile for
 *     "checked for".
 *
 *   MEMBER_ID_IDLE_MS        a TYPED member ID is checked on blur, or after this long with no
 *     further change (the SEARCH_DEBOUNCE_MS pattern). A filled one (card scan, a discovery
 *     candidate, a region pick) is checked at once, as before.
 *
 *   limitNoticeOf / limitNoticeKey / recheckBlocked / limitTimeLabel (PROD-691): the notice a
 *     limit answer puts under EligibilityBadge, and whether Re-check is offered meanwhile.
 *
 *   limitedResultFor / verdictWithheld (Q-C5): a limit answer for a CHANGED member ID shows no
 *     coverage verdict (the stored result was checked for another ID), only when it can be checked.
 *
 * ★★★ REUSE (PROD-936 PAT-2, Alex 2026-10-13 "Sparpaket", decision 2). orella-api (API-2, live since
 *   v243.356.0) answers a PATIENT's check from the stored result while the same coverage was checked
 *   under PATIENT_ELIGIBILITY_REUSE_DAYS (30) ago — no payer call:
 *     HTTP 200 { eligibility: <stored>, error: 'recheck_limit', reused: true, checkedAt, nextCheckAt }
 *   with NO availableAt (an older build reads it as "could not be checked just now - this is the last
 *   result"). The server proved the coverage (its coverageKey), so the result is THIS coverage's:
 *   eligibilityAnswer carries reused / checkedAt / nextCheckAt, limitNoticeOf gives
 *   { kind: 'reused', checkedAt, nextCheckAt }, limitNoticeKey maps it to eligibility.reusedNotice
 *   (approved English, Alex 2026-10-13 Q46; es withheld until Dr. Rami), recheckBlocked holds Re-check
 *   back until nextCheckAt, and limitNoticeParams fills {{when}} / {{date}}. A reused answer is never
 *   "fresh" (every surface that says "Verified ... just now" asks for no notice).
 *
 * Pure module, no imports: the gates execute it directly.
 */

export const MEMBER_ID_IDLE_MS = 1500

export const LIMIT_ERRORS = Object.freeze({ recheck_limit: 'limit', recheck_unavailable: 'unavailable' })

export function eligibilityAnswer(resp) {
  const r = resp && typeof resp === 'object' ? resp : {}
  const eligibility = r.eligibility && typeof r.eligibility === 'object' ? r.eligibility : null
  const limited = Object.prototype.hasOwnProperty.call(LIMIT_ERRORS, r.error) ? LIMIT_ERRORS[r.error] : null
  const availableAt = limited === 'limit' && typeof r.availableAt === 'string' && r.availableAt ? r.availableAt : null
  // PROD-936 PAT-2: the 30-day reuse answer (see REUSE below). Only then do the three extra keys appear,
  //   so every other answer keeps its exact shape.
  const isoOf = (v) => (typeof v === 'string' && v && Number.isFinite(Date.parse(v)) ? v : null)
  if (limited === 'limit' && r.reused === true && eligibility && isoOf(r.checkedAt) && isoOf(r.nextCheckAt)) {
    return { eligibility, limited, availableAt, reused: true, checkedAt: r.checkedAt, nextCheckAt: r.nextCheckAt }
  }
  return { eligibility, limited, availableAt }
}

// A JS copy of payerPick.js isRoutablePayerId (rcm_service._has_passthrough_payer_id on one value).
// Copied, not imported: the gates load this module directly and as in-memory mutants (data: URLs,
// where a relative import does not resolve). check-eligibility-trigger ET-15 (p) holds the copy
// equal to payerPick's on a table of values.
function routablePayerId(v) {
  const s = (v === undefined || v === null ? '' : String(v)).trim()
  if (/^[0-9]+$/.test(s) && s.length >= 4 && s.length <= 6) return true
  return s.length >= 2 && s.length <= 8 && !/\s/.test(s) && s === s.toUpperCase() && s !== s.toLowerCase()
}

const carrierName = (v) => String(v || '').trim().replace(/\s+/g, ' ').toLowerCase()

/**
 * 'carrier|memberId', or null without a carrier name and a member ID.
 * carrier: providerId when it is a routable payer id (the server routes the 270 by it). Otherwise
 * (a typed carrier's 'custom', a registry or discovery slug, none) the server re-resolves the payer
 * from the NAME (ensure_stedi_payer_id), so the carrier is providerId plus the normalized name.
 * Review A2 / B1 (2026-10-09), measured at a9ffb5ed: every typed carrier had providerId 'custom', so
 * 'Synthetic Blue' and 'Synthetic Green' with one member ID gave the same key 'custom|SYN-A': a
 * re-typed carrier was "already checked" in Profile Save and in onboarding, and the old carrier's
 * result was drawn as the new one's (review proofs S2, S3).
 * PROD-822 (review r1 C2 / r2 "decide 3", 2026-10-09): when the patient is NOT the policyholder,
 * the 270 subscriber is the policyholder (_build_270_payload), so the key also carries the
 * relationship and the policyholder's first name, last name and date of birth:
 * 'carrier|memberId|relationship|first|last|dob'. A 'Self' (or empty) relationship keeps the plain
 * 'carrier|memberId'. Measured at 589f548a: onboarding keyed only carrier|memberId, so a
 * policyholder corrected after a check (a data mismatch fixed) counted as checked: the section
 * save paid nothing for it and Back drew the old answer. Profile's eligibilityInputsKey already
 * carried it; this is now one rule for both.
 */
export function eligibilityKey(ins) {
  const i = ins && typeof ins === 'object' ? ins : {}
  if (!i.provider || !i.memberId) return null
  const carrier = routablePayerId(i.providerId) ? i.providerId : `${String(i.providerId || '').trim()}:${carrierName(i.provider)}`
  const rel = String(i.subscriberRelationship || '').trim().toLowerCase()
  if (rel === '' || rel === 'self') return `${carrier}|${i.memberId}`
  const s = (v) => String(v || '').trim()
  return `${carrier}|${i.memberId}|${rel}|${s(i.subscriberFirstName)}|${s(i.subscriberLastName)}|${s(i.subscriberDOB || i.subscriberDob)}`
}

/**
 * PROD-691 (P4-3): the notice a limit answer puts under the badge, or null.
 *   'limit' with a time  -> { kind: 'limit', availableAt }   "Coverage was checked recently - … after {{time}}."
 *   'limit' with no time, or 'unavailable' -> { kind: 'unavailable', availableAt: null }
 *                                             "Coverage could not be checked just now - …"
 * A 'limit' without a time cannot say when, so it says the one true thing it can.
 */
export function limitNoticeOf(answer) {
  const a = answer && typeof answer === 'object' ? answer : {}
  if (a.reused === true && a.limited === 'limit' && a.checkedAt && a.nextCheckAt) return { kind: 'reused', checkedAt: a.checkedAt, nextCheckAt: a.nextCheckAt } // PROD-936
  if (a.limited === 'limit' && a.availableAt && Number.isFinite(Date.parse(a.availableAt))) return { kind: 'limit', availableAt: a.availableAt }
  if (a.limited === 'limit' || a.limited === 'unavailable') return { kind: 'unavailable', availableAt: null }
  return null
}

/**
 * The i18n key of a notice from limitNoticeOf / limitedResultFor, or null. An 'otherId' notice
 * says only when the member ID can be checked; without a time it has nothing true to say (null).
 */
export function limitNoticeKey(notice) {
  if (!notice) return null
  if (notice.kind === 'otherId') return notice.availableAt ? 'eligibility.limitOtherMemberId' : null
  if (notice.kind === 'reused') return 'eligibility.reusedNotice' // PROD-936 NEW-1
  return notice.kind === 'limit' ? 'eligibility.limitCheckedToday' : 'eligibility.limitUnavailable'
}

/**
 * Q-C5 (PROD-691; Alex 2026-10-07 ~22:30, "Ja, kein altes Ergebnis"). At the limit the server can
 * only answer its STORED result, which was checked for the coverage of the last real answer. For
 * a CHANGED member ID that result is not this ID's: no coverage verdict is shown, only when this
 * member ID can be checked ("You can check this member ID again after {{time}}.").
 *
 *   limitedResultFor(answer, checkedKey, resultKey) -> { eligibility, notice, resultKey }
 *     answer      eligibilityAnswer(resp)
 *     checkedKey  eligibilityKey of the coverage this check asked about
 *     resultKey   eligibilityKey of the coverage the shown result was checked for (null: not known)
 *   * Not a limit answer: as before (`eligibility` may be null: the card stays as it was). A real
 *     result belongs to checkedKey from now on.
 *   * A limit answer for the coverage of the shown result (the same member ID): the stored result
 *     and the PROD-691 notice, unchanged.
 *   * A limit answer for any other coverage, or when the result's coverage is not known:
 *     eligibility null and the notice { kind: 'otherId', availableAt } (availableAt null when the
 *     answer gave no time). The caller shows NO verdict: an 'otherId' notice withholds it.
 *   resultKey never moves on a limit answer: the server's stored result is still the old one.
 * The client sends nothing new: both keys are what it already knows.
 */
export function limitedResultFor(answer, checkedKey, resultKey) {
  const a = answer && typeof answer === 'object' ? answer : {}
  const eligibility = a.eligibility && typeof a.eligibility === 'object' ? a.eligibility : null
  const known = resultKey || null
  if (a.limited !== 'limit' && a.limited !== 'unavailable') {
    return { eligibility, notice: null, resultKey: eligibility ? (checkedKey || null) : known }
  }
  // PROD-936: a reuse answer is the stored result of the coverage on file NOW (the server compared its
  //   coverageKey), so it is shown for the checked coverage, with its age, whatever was on the card.
  //   The notice carries the coverage it answered (`key`), so a screen whose form has moved on since
  //   (noticeForCoverage) offers Re-check again: "or sooner if you change your insurance".
  const reused = limitNoticeOf(a)
  if (reused && reused.kind === 'reused' && eligibility) return { eligibility, notice: { ...reused, key: checkedKey || known || null }, resultKey: checkedKey || known }
  if (checkedKey && checkedKey === known) return { eligibility, notice: limitNoticeOf(a), resultKey: known }
  const n = limitNoticeOf(a)
  return { eligibility: null, notice: { kind: 'otherId', availableAt: n && n.kind === 'limit' ? n.availableAt : null }, resultKey: known }
}

/**
 * PROD-713 (2026-10-09): true when the onboarding store already holds this coverage's check, so a
 * plain re-mount of the insurance step (Continue then Back, or the profile run opened again with
 * coverage on file) pays nothing. Measured at 713dc775: every such mount sent one PUT /profile and
 * one paid check of an unchanged member ID, because the step's "checked once" memory was a ref
 * that every mount starts empty.
 *   state  useOnboardingStore.getState()
 *   key    eligibilityKey of the coverage the step would check
 * True when
 *   * state.eligibilityCheckedKey === key and that check is still OUT (inFlightRun): a check of
 *     this coverage was sent in this run and the step will draw its answer. Review B-S2
 *     (2026-10-09): once it is answered, only a result counts. Measured at ddcc3919: a soft fail
 *     (HTTP 200 { eligibility: null, error }, e.g. a Stedi timeout) kept the key, so every later
 *     mount paid nothing, drew no badge and offered no Re-check for the rest of the run. A
 *     coverage whose only check was OnboardingFlow's silent section save (its answer is never
 *     read) is likewise not "checked" for the step; or
 *   * state.eligibilityResultKey === key and insurance.last_eligibility holds a result: the result
 *     on the card was checked for this coverage IN THIS RUN. PROD-822 (review B-r2-2): a result
 *     loaded with the profile (on resume) has no key and is not counted: the Profile autosave, the
 *     unmount save and md staff edits change the member ID without a check while the server keeps
 *     last_eligibility, so it may be an older member ID's. Measured at 589f548a: the step's Q-C5
 *     mount effect gave it the coverage on file's key, so the step ran no check and showed the old
 *     ID's verdict as the new one's (unless loadedResultKey proves it is this coverage's: review
 *     REVIEW_PNEXT2 B-1). Nor is a transient result (payer_unreachable, a pre-class
 *     error; transientResult, review C1 / r2 "decide 2"): the step retries it once, as Profile's
 *     Save does.
 * Re-check is the patient asking: it never reads this.
 */
export function coverageAlreadyChecked(state, key) {
  const s = state && typeof state === 'object' ? state : {}
  if (!key) return false
  if (s.eligibilityCheckedKey === key && inFlightRun(s, key)) return true
  const le = s.insurance && typeof s.insurance === 'object' ? s.insurance.last_eligibility : null
  const known = s.eligibilityResultKey === key || (!s.eligibilityResultKey && loadedResultKey(s.insurance) === key) // review REVIEW_PNEXT2 B-1
  return known && !!le && typeof le === 'object' && !transientResult(le)
}

/**
 * PROD-713 review B-S1 (2026-10-09): the stored result the onboarding insurance step may DRAW for
 * this coverage when it mounts, or null. The step can leave the screen while its check is out
 * (Continue, then Back), so its first render reads the store, and the store can hold the result of
 * a member ID the patient has since changed. Measured at 40125e7d: ID A answered, ID changed to B,
 * Back drew A's verdict as B's (Q-C5, Alex 2026-10-07: "kein altes Ergebnis").
 *   state  useOnboardingStore.getState()
 *   key    eligibilityKey of the coverage the step shows
 * insurance.last_eligibility when it was checked for `key` in this run. Otherwise null.
 * PROD-822 (review B-r2-2): a result whose coverage is not known (loaded with the profile on
 * resume: eligibilityResultKey null) is not drawn as the coverage on file's. Measured at 589f548a:
 * member ID A -> B in Profile, left with Back (stored, no check); the health-profile run then drew
 * A's verdict on the insurance step as B's. The step checks the coverage once instead.
 */
export function storedResultFor(state, key) {
  const s = state && typeof state === 'object' ? state : {}
  const le = s.insurance && typeof s.insurance === 'object' ? s.insurance.last_eligibility : null
  if (!key || !le || typeof le !== 'object') return null
  if (s.eligibilityResultKey === key) return le
  // review REVIEW_PNEXT2 B-1: a loaded result that names its own subscriber, matching this coverage
  return !s.eligibilityResultKey && loadedResultKey(s.insurance) === key ? le : null
}

/**
 * PROD-822 review REVIEW_PNEXT2 B-1 (2026-10-10, cost bound): the eligibilityKey a result LOADED
 * with the profile (no key known in this run) provably belongs to, or null.
 * MEASURED (orella-api GitHub main 0a07b67, rcm_service._parse_271_response and the persist in
 * trigger_practice_eligibility's direct path): last_eligibility carries NO inputs key, no payer id
 * and no request member ID. The one trace of what was asked is `subscriberConfirmed`
 * { firstName, lastName, dob, memberId }: the subscriber the payer's 271 named, written only when
 * the 271 carries a subscriber name (a real coverage answer; a not_found, a transient error or a
 * payer_not_mapped result has none).
 * CHOSEN: the loaded result is the coverage on file's when
 *   * it is not transient (transientResult), and carries subscriberConfirmed.memberId;
 *   * that member ID equals the one on file (case, spaces and punctuation ignored);
 *   * for a dependent's policy (the subscriber is the policyholder): the policyholder's DOB on file
 *     equals subscriberConfirmed.dob (YYYY-MM-DD vs the 271's YYYYMMDD), both present.
 * Anything else stays unknown and the step checks once (B-r2-2). The payer itself is not in the
 * result, so a carrier change that keeps the very same member ID is not seen here: member IDs are
 * issued per payer, and a Profile carrier change with a new ID is caught by the member ID.
 */
export function loadedResultKey(ins) {
  const i = ins && typeof ins === 'object' ? ins : {}
  const le = i.last_eligibility
  if (!le || typeof le !== 'object' || transientResult(le)) return null
  const key = eligibilityKey(i)
  const sc = le.subscriberConfirmed
  if (!key || !sc || typeof sc !== 'object') return null
  const normId = (v) => String(v || '').toUpperCase().replace(/[^A-Z0-9]/g, '')
  if (!normId(sc.memberId) || normId(sc.memberId) !== normId(i.memberId)) return null
  const rel = String(i.subscriberRelationship || '').trim().toLowerCase()
  if (rel !== '' && rel !== 'self') {
    const dob = (v) => String(v || '').replace(/[^0-9]/g, '')
    if (dob(i.subscriberDOB).length !== 8 || dob(i.subscriberDOB) !== dob(sc.dob)) return null
  }
  return key
}

/**
 * PROD-975 (2026-10-14): may the check-in step 2 keep the STORED result instead of asking the payer
 * again? Measured at 7873ed00: it did whenever the result was under 12 hours old and for the same
 * doctor, and never looked at the insurance; a member ID edited after the result (Profile's own
 * check failed) showed the old ID's verdict under the new ID, and a carrier or policyholder change
 * with the same member ID likewise.
 *   existing  insurance.last_eligibility
 *   ins       the insurance on file now
 *   doctor    { npi }
 *   windowMs  the reuse window (CHECKIN_RECHECK_WINDOW_MS, 12 hours)
 *   nowMs     the clock (default Date.now())
 * True only when ALL hold: a checkedAt younger than windowMs; the same doctor NPI; the result is
 * not transient (transientResult: the payer was not reachable, so there is nothing to keep); and
 * the result PROVABLY belongs to the insurance on file, by one of
 *   * `existing.checkedKey` (the key the server stamps from the insurance it asked, or the tag this
 *     modal puts on its own answer) equals the key of the insurance now: eligibilityKey, or the
 *     member-ID stand-in `|<member ID>` when there is no carrier name (the same stand-in the
 *     step-2 run key uses);
 *   * no checkedKey, and loadedResultKey(ins) equals eligibilityKey(ins): the member-ID proof from
 *     the 271's subscriberConfirmed (PROD-822). Never without a carrier name (key null).
 * A checkedKey that differs is NOT rescued by loadedResultKey: the key says what was asked.
 * Anything else is false: one paid check, the safe side (a key port that drifts costs only a check).
 * A limit answer's stored result must not be tagged by the caller (it was checked for whatever
 * the server last asked); the caller tags only a real answer of its own check.
 */
export function storedResultStillFits(existing, ins, doctor, windowMs, nowMs) {
  const le = existing && typeof existing === 'object' ? existing : null
  const i = ins && typeof ins === 'object' ? ins : {}
  if (!le || !le.checkedAt || !doctor || le.forDoctorNpi !== doctor.npi) return false
  const ageMs = (Number.isFinite(nowMs) ? nowMs : Date.now()) - new Date(le.checkedAt).getTime()
  if (!(ageMs < windowMs) || transientResult(le)) return false
  const key = eligibilityKey(i)
  if (le.checkedKey !== undefined && le.checkedKey !== null && le.checkedKey !== '') {
    const want = key || (i.memberId ? `|${i.memberId}` : null)
    return !!want && le.checkedKey === want
  }
  return !!key && loadedResultKey(i) === key
}

/**
 * PROD-713 review B-S1: the onboarding step's check of this coverage that is still out
 * (state.eligibilityRun, { key, put, done }), or null. A step mounted while it is out draws its
 * answer instead of paying for another check.
 */
export function inFlightRun(state, key) {
  const s = state && typeof state === 'object' ? state : {}
  const run = s.eligibilityRun
  return key && run && typeof run === 'object' && run.key === key ? run : null
}

/**
 * PROD-821 (review B-r2-1, 2026-10-09): the onboarding store forgets every check of `key` it holds,
 * because the payer that key's checks went to is no longer the payer on file. eligibilityKey keys a
 * non-routable carrier (typed 'custom', a slug, a discovery pick without a registry slug) by its
 * NAME, but the 270 routes by payerId first: a discovered plan with the same normalized name and
 * member ID and ANOTHER payerId has the same key. Measured at 589f548a (review proof_r2 R2): typed
 * "Synthetic Regional Plan" (the server stamped payerId SYNX1), not_found; "Use this plan" on a
 * candidate with the same name, payerId SYNY2 -> coverageAlreadyChecked true, no check, and SYNX1's
 * not_found shown for a plan that now routes to SYNY2.
 *   st   useOnboardingStore.getState()
 *   key  eligibilityKey of the coverage now on file
 * A check of `key` still out is marked stale (its answer is neither stored nor drawn) and no longer
 * counts as out; the checked key and the result key are cleared when they are `key`, and the stored
 * result with them (or when no key is known for it). Returns true when it forgot a result or a
 * check. Nothing is sent: the step's own effect then checks the plan once.
 */
export function forgetCoverageCheck(st, key) {
  if (!st || typeof st !== 'object' || !key) return false
  let forgot = false
  const run = inFlightRun(st, key)
  if (run) { run.stale = true; st.setEligibilityRun(null); forgot = true }
  if (st.eligibilityCheckedKey === key) { st.setEligibilityCheckedKey(null); forgot = true }
  const le = st.insurance && typeof st.insurance === 'object' ? st.insurance.last_eligibility : null
  if (st.eligibilityResultKey === key || (!st.eligibilityResultKey && le)) {
    st.setEligibilityResultKey(null)
    if (le) st.setInsurance({ last_eligibility: null })
    forgot = true
  }
  return forgot
}

// Review B2: a Self policy's subscriber as the server reads it from the profile's identification.
function selfSubscriber(patient) {
  const pt = patient && typeof patient === 'object' ? patient : {}
  const s = (v) => String(v || '').trim()
  return [s(pt.firstName || pt.first_name), s(pt.lastName || pt.last_name), s(pt.dateOfBirth || pt.dob || pt.date_of_birth)]
}

/**
 * PROD-1196 (2026-10-19): true when the subscriber a check would ask the payer about has a blank first
 * name, last name or date of birth. Profile sections open in any order now, so Insurance can be saved
 * before Identification; the server then builds a 270 with an empty name and birth date (a wasted,
 * failed Stedi call stored as an error on the card). For a Self policy the subscriber is the patient's
 * identification; otherwise it is the policyholder fields on the insurance form.
 *   ins      the insurance the save sent
 *   patient  the profile's identification
 */
export function subscriberDetailsBlank(ins, patient) {
  const i = ins && typeof ins === 'object' ? ins : {}
  const s = (v) => String(v || '').trim()
  const rel = s(i.subscriberRelationship || 'Self').toLowerCase()
  const parts = rel === '' || rel === 'self'
    ? selfSubscriber(patient)
    : [s(i.subscriberFirstName), s(i.subscriberLastName), s(i.subscriberDOB || i.subscriberDob)]
  return parts.some((v) => !v)
}

/**
 * PROD-713, Profile half (2026-10-09): what one paid check is ASKED about, as a string, or null
 * without a carrier and a member ID. orella-api rcm_service._build_270_payload reads the member ID,
 * the payer (eligibilityKey: a routable providerId, else providerId plus the carrier name, since the
 * server re-resolves a typed carrier from its name; review A2) and, when the patient is not
 * the policyholder, the policyholder's first name, last name and date of birth. Group number,
 * copays, card photos and coverage type are not in the request: an edit of those asks the payer
 * nothing new.
 *   patient  the profile's identification section. Review B2 (2026-10-09): for a 'Self' policy
 *            the 270 subscriber IS the patient (_build_270_payload: identification firstName /
 *            first_name, lastName / last_name, dateOfBirth / dob / date_of_birth), so a corrected
 *            name or date of birth asks the payer something new. Measured at a9ffb5ed: a Save after
 *            a DOB correction paid no check while the card kept the old name/DOB mismatch.
 */
export function eligibilityInputsKey(ins, patient) {
  const i = ins && typeof ins === 'object' ? ins : {}
  const key = eligibilityKey(i)
  if (!key) return null
  const rel = String(i.subscriberRelationship || 'Self').trim().toLowerCase()
  const s = (v) => String(v || '').trim()
  if (rel === '' || rel === 'self') return JSON.stringify([key, 'self', ...selfSubscriber(patient)])
  return JSON.stringify([key, rel, s(i.subscriberFirstName), s(i.subscriberLastName), s(i.subscriberDOB || i.subscriberDob)])
}

/**
 * PROD-713 review A1 (2026-10-09): the record of ONE real answer the screen received in this mount:
 * { inputs: eligibilityInputsKey of what that check asked, result: the result it answered }, or
 * null without inputs or a result. The only thing storedResultCovers accepts as "checked for".
 *   body         the insurance the check was asked about (the save that preceded it)
 *   eligibility  the real result it answered (never a limit answer's stored result)
 *   patient      the profile's identification when the check was asked (review B2)
 * Why not the record the screen loads: its last_eligibility carries no member ID, and the C5
 * autosave, the unmount save and md staff edits change the member ID without a check while the
 * server keeps last_eligibility (a shallow merge). Measured at a9ffb5ed (review proof S1): member
 * ID A -> B saved on leave, reopened, Save -> no check, A's verdict shown as B's.
 */
export function answeredCheck(body, eligibility, patient) {
  const inputs = eligibilityInputsKey(body, patient)
  if (!inputs || !eligibility || typeof eligibility !== 'object') return null
  return { inputs, result: eligibility }
}

/**
 * Review C1 (2026-10-09): true for a result that says the payer could not be asked just now, not
 * what the coverage is: resultClass 'payer_unreachable' (orella-api rcm_service: AAA 42 / 80, a
 * Stedi top-level error such as a timeout, an empty response; "retry-worthy"). A result with no
 * resultClass and status 'error' (written before the classes existed) cannot be told apart and is
 * treated the same. Such a result never makes a Save skip its check: Save retries it, as before.
 */
function transientResult(le) {
  const cls = typeof le.resultClass === 'string' ? le.resultClass : ''
  return cls === 'payer_unreachable' || (!cls && le.status === 'error')
}

/**
 * PROD-713, Profile half: true when Profile -> Insurance's Save needs NO paid check, because the
 * result on the card was checked IN THIS MOUNT for exactly what this save would ask about.
 * Measured at 5244fc2e: every successful Save paid one check, also for an unchanged member ID (a
 * copay or group number edit, or Save with nothing changed).
 *   insurance  the profile store's insurance section (insurance.last_eligibility: the card)
 *   resultKey  eligibilityKey the card's result was checked for (Q-C5 resultKeyRef)
 *   answered   answeredCheck(...) of the last real answer in this mount (null: none yet)
 *   body       the insurance this save sent
 *   patient    the profile's identification now (review B2: a Self policy's subscriber)
 * True only when the card still shows THAT answer (the same object, or the same checkedAt), it is
 * not a transient error (transientResult, review C1), and this save asks the same. A result loaded with the record, replaced since, or checked for another
 * member ID, policyholder or carrier still gets the one check after Save, as before (review A1).
 * Re-check is the patient asking: it never reads this.
 */
export function storedResultCovers(insurance, resultKey, answered, body, patient) {
  const le = insurance && typeof insurance === 'object' ? insurance.last_eligibility : null
  if (!le || typeof le !== 'object') return false
  if (transientResult(le)) return false
  if (!answered || typeof answered !== 'object' || !answered.inputs) return false
  const res = answered.result
  if (!res || typeof res !== 'object') return false
  const sameResult = le === res || (typeof le.checkedAt === 'string' && le.checkedAt !== '' && le.checkedAt === res.checkedAt)
  if (!sameResult) return false
  const key = eligibilityKey(body)
  return !!key && resultKey === key && answered.inputs === eligibilityInputsKey(body, patient)
}

/**
 * PROD-794 (2026-10-09): OnboardingFlow's section-save check stores its answer the way the insurance
 * step's runEligibility does, so Back draws it instead of paying for a second check. Measured at
 * 5244fc2e: a member ID typed and Continue tapped with no blur was checked once by the section save
 * (its answer never read), and Back paid one more check of the same coverage (the step had nothing
 * to draw for it; review B-S2 made that the documented consequence).
 *   st          useOnboardingStore.getState() when the answer arrives (setEligibilityResultKey,
 *               setInsurance)
 *   checkedKey  eligibilityKey of the coverage the section save checked
 *   answer      eligibilityAnswer(resp)
 *   run         the section save's in-flight marker; a run marked stale by forgetCoverageCheck
 *               (PROD-821: another payer under the same key) stores nothing
 * The same rules as runEligibility: an answer for a coverage the patient has changed since is
 * neither stored nor drawn; Q-C5 (limitedResultFor) decides the result key; a withheld verdict
 * clears the stored copy; a real result (or a same-ID limit answer's stored result) is stored; a
 * soft fail stores nothing, so Back checks once more (review B-S2). Returns limitedResultFor's
 * answer, or null when nothing was applied.
 */
export function storeEligibilityAnswer(st, checkedKey, answer, run = null) {
  if (!st || typeof st !== 'object' || !checkedKey) return null
  if (run && run.stale) return null // PROD-821: the payer changed under the same key while it was out
  if (eligibilityKey(st.insurance) !== checkedKey) return null
  const shown = limitedResultFor(answer, checkedKey, st.eligibilityResultKey)
  st.setEligibilityResultKey(shown.resultKey)
  if (verdictWithheld(shown.notice)) st.setInsurance({ last_eligibility: null })
  else if (shown.eligibility) st.setInsurance({ last_eligibility: shown.eligibility })
  return shown
}

/**
 * PROD-936: the notice a screen with an editable form shows now. A 'reused' notice answered one coverage
 * (its `key`, from limitedResultFor); once the form names another (a new carrier, member ID or
 * policyholder) the notice no longer holds — and with it the Re-check it held back — so null. Any other
 * notice, or a reused one without a key, is returned as it is.
 */
export function noticeForCoverage(notice, ins) {
  if (!notice || notice.kind !== 'reused' || !notice.key) return notice || null
  return eligibilityKey(ins) === notice.key ? notice : null
}

/** True when a notice withholds the coverage verdict (Q-C5: a changed member ID at the limit). */
export function verdictWithheld(notice) {
  return !!notice && notice.kind === 'otherId'
}

/**
 * True while a 'limit' notice's next-check time (availableAt), or a 'reused' notice's (nextCheckAt,
 * PROD-936), is still ahead: Re-check is not offered.
 */
export function recheckBlocked(notice, now = Date.now()) {
  if (!notice || (notice.kind !== 'limit' && notice.kind !== 'reused')) return false
  const at = Date.parse(notice.kind === 'reused' ? notice.nextCheckAt : notice.availableAt)
  return Number.isFinite(at) && at > now
}

/**
 * PROD-936: a reuse notice's dates, "November 16, 2026" / "16 de noviembre de 2026" in the UI language's
 * locale ('' without a date). limitTimeLabel covers at most 7 days (PROD-1164); the reuse window is 30 days.
 * ★ PROD-1164 review F1: month 'long', never 'short'. With 'short', JavaScriptCore (Safari, the iOS
 * WebView) orders es-US month-first ("nov 16, 2026"), and es reusedNotice "después del {{date}}" needs
 * the day number right after "del". 'long' is day-first in es-US in every engine.
 */
export function reuseDateLabel(iso, locale = 'en-US') {
  const d = new Date(iso || '')
  if (!iso || !Number.isFinite(d.getTime())) return ''
  return d.toLocaleDateString(locale, { month: 'long', day: 'numeric', year: 'numeric' })
}

/**
 * PROD-1133 (Alex 2026-10-18): the SECONDARY save's reuse answer. The save route answers
 * { secondary, eligibility: <stored>, reused: true, checkedAt, nextCheckAt } (no `error`, unlike the
 * re-check route) when it reused the stored result for the same coverage. { checkedAt, nextCheckAt } or
 * null: both dates must parse; the client never computes either one.
 */
export function secondarySaveReuseOf(resp) {
  const r = resp && typeof resp === 'object' ? resp : {}
  const ok = (v) => typeof v === 'string' && v && Number.isFinite(Date.parse(v))
  if (r.reused === true && r.eligibility && typeof r.eligibility === 'object' && ok(r.checkedAt) && ok(r.nextCheckAt)) {
    return { checkedAt: r.checkedAt, nextCheckAt: r.nextCheckAt }
  }
  return null
}

/**
 * "October 12" / "12 de octubre" in the UI language's locale (no year; the window is 30 days), '' without
 * a date. Month 'long' for the same reason as reuseDateLabel (es "el {{date}}" on iOS, PROD-1164 F1).
 */
export function secondaryReuseDate(iso, locale = 'en-US') {
  const d = new Date(iso || '')
  if (!iso || !Number.isFinite(d.getTime())) return ''
  return d.toLocaleDateString(locale, { month: 'long', day: 'numeric' })
}

/** The t() values of cob.secondaryReusedLine: {{checked}} and {{date}} (the next check). */
export function secondaryReuseParams(reuse, locale = 'en-US') {
  const r = reuse && typeof reuse === 'object' ? reuse : {}
  return { checked: secondaryReuseDate(r.checkedAt, locale), date: secondaryReuseDate(r.nextCheckAt, locale) }
}

/**
 * The t() values for limitNoticeKey(notice). 'reused' (eligibility.reusedNotice): {{when}} the check's
 * age ("3 days ago": `relative(checkedAt)`, the badge's fmtRelative; else its date) and {{date}} when
 * the next check is possible. Every other notice: {{time}} (limitTimeLabel), as before.
 */
export function limitNoticeParams(notice, locale = 'en-US', relative = null, now = new Date()) {
  const n = notice && typeof notice === 'object' ? notice : {}
  if (n.kind === 'reused') {
    const rel = typeof relative === 'function' ? relative(n.checkedAt) : ''
    return { when: rel || reuseDateLabel(n.checkedAt, locale), date: reuseDateLabel(n.nextCheckAt, locale) }
  }
  return { time: limitTimeLabel(n.availableAt, locale, now) }
}

/**
 * The next-check time in the patient's local time, always with its date: "October 26 at 9:59 AM"
 * (en-US), "26 de octubre a las 9:59 a.m." (es-US). It is at most 7 days away (PROD-1164: 5 checks
 * per 7 days), so a bare time or a weekday alone would be ambiguous. ★ Month 'long', never 'short':
 * with 'short', JavaScriptCore (Safari, the iOS WebView) orders es-US month-first, and es "a partir
 * del {{time}}" would read "del oct 26, 9:59 a.m." (review F1). 'long' is day-first in every engine. `locale` is the UI language's; `now` is kept for
 * the callers' signature (the label no longer depends on it).
 */
export function limitTimeLabel(iso, locale = 'en-US', now = new Date()) {
  const d = new Date(iso)
  if (!Number.isFinite(d.getTime())) return ''
  return d.toLocaleString(locale, { month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}
